import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Shared shell for public-share list sections. Standardises the card wrapper,
 * header row (icon + title + optional count), and loading/empty states so the
 * per-section components only own their row/card rendering logic.
 */
export function PublicSection({
  title,
  icon: Icon,
  count,
  isLoading,
  emptyMessage,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  count?: number;
  isLoading?: boolean;
  emptyMessage?: string;
  children?: ReactNode;
}) {
  if (isLoading) {
    return (
      <section className="rounded-lg border bg-card p-5">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }

  const isEmpty = count === 0 && emptyMessage !== undefined;

  return (
    <section className="rounded-lg border bg-card p-5">
      <header className="mb-3 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <h3 className="text-base font-semibold">{title}</h3>
        {count !== undefined && (
          <span className="text-xs text-muted-foreground">({count})</span>
        )}
      </header>
      {isEmpty ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        children
      )}
    </section>
  );
}
